import React, { useState } from 'react';
import { XCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { customerCancelBooking, driverCancelBooking } from '../services/api';

/**
 * Cancel a confirmed "Pay Driver Directly" booking.
 *  - mode="customer": refund follows the 3-cancellation cycle.
 *  - mode="driver": customer is always refunded in full.
 */
export const CancelBookingButton = ({ mode, delivery, onDone, className = '' }) => {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const isDriver = mode === 'driver';

  const submit = async () => {
    setBusy(true);
    try {
      const call = isDriver ? driverCancelBooking : customerCancelBooking;
      const { data } = await call(delivery.id, { reason: reason.trim() });
      toast.success(data.message || 'Cancelled');
      setOpen(false);
      setReason('');
      onDone?.(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not cancel this booking');
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        className={`gap-1.5 text-red-700 border-red-200 hover:bg-red-50 ${className}`}
        data-testid={`cancel-booking-btn-${delivery.id}`}
      >
        <XCircle className="w-4 h-4" /> {isDriver ? 'Cancel this job' : 'Cancel booking'}
      </Button>
    );
  }

  return (
    <div className="rounded-lg border-2 border-red-200 bg-red-50/60 p-3 space-y-2" data-testid={`cancel-booking-panel-${delivery.id}`}>
      <p className="text-sm font-semibold text-red-800">
        {isDriver ? 'Cancel this job?' : 'Cancel this booking?'}
      </p>
      <p className="text-xs text-red-800 leading-snug">
        {isDriver
          ? 'The customer will be refunded their full online payment (Booking Deposit + JustDeliver Driver Fee) and this will be recorded as a driver cancellation.'
          : 'Your first two cancellations are refunded in full (Booking Deposit + JustDeliver Driver Fee). On the third cancellation your online payment is not refunded and your counter resets.'}
      </p>
      <Textarea
        rows={2}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder={isDriver ? 'Why can you no longer do this job?' : 'Why are you cancelling?'}
        className="text-sm bg-white"
        data-testid={`cancel-booking-reason-${delivery.id}`}
      />
      <div className="flex gap-2">
        <Button variant="outline" size="sm" className="flex-1" onClick={() => setOpen(false)} disabled={busy}
                data-testid={`cancel-booking-back-${delivery.id}`}>
          Keep booking
        </Button>
        <Button
          size="sm"
          className="flex-1 bg-red-600 hover:bg-red-700 text-white"
          onClick={submit}
          disabled={busy || reason.trim().length < 5}
          data-testid={`cancel-booking-confirm-${delivery.id}`}
        >
          {busy ? <><Loader2 className="w-4 h-4 animate-spin mr-1" /> Cancelling…</> : 'Confirm cancellation'}
        </Button>
      </div>
    </div>
  );
};

export default CancelBookingButton;
